import React from 'react';
import { Button, Spin } from 'antd';
import { Merge, X, CopyCheck, Copy } from 'lucide-react';
import { fmtVZ } from '../../../utils/timeUtils';
import { validateBarcode } from '../../../utils/barcodeValidation';
import { EmptyState, StatusTag } from '../../common';
import { BarcodeIcon } from './AuditFlagChips';
import AuditImageThumb from './AuditImageThumb';

function ProductCompareCard({ product, onMarkDuplicate, onRecordClick }) {
    const scan = product.latestScan;
    const invalidCode = !validateBarcode(product.code).valid;

    return (
        <div className="h-full rounded-lg border border-surface-200 bg-white p-3">
            <div className="flex gap-3">
                <AuditImageThumb record={scan || { imageCount: 0 }} size={72} onClick={scan?._id ? () => onRecordClick?.(scan) : undefined} />
                <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-semibold text-surface-900">{product.name}</div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <BarcodeIcon barcodeMeta={validateBarcode(product.code)} hasCatalogMatch />
                        <span className="text-xs text-surface-500 tabular-nums">{product.code}</span>
                        {invalidCode && <StatusTag tone="danger">Código inválido</StatusTag>}
                    </div>
                    <div className="mt-1 text-xs text-surface-500 truncate">
                        {product.category || '—'} · {product.segment || '—'}
                    </div>
                    <div className="text-xs text-surface-400 tabular-nums">{fmtVZ(product.updatedAt)}</div>
                    <Button size="small" className="mt-2" icon={<Copy />} onClick={() => onMarkDuplicate?.(product)}>
                        Marcar como duplicado
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default function AuditDuplicateGroups({ groups, loading, onMergeGroup, onMarkDuplicate, onRecordClick, onDismiss }) {
    if (loading) {
        return (
            <div className="flex items-center justify-center gap-3 py-10 text-sm text-surface-500">
                <Spin size="small" />
                <span>Cargando duplicados…</span>
            </div>
        );
    }
    if (!groups?.length) {
        return <EmptyState icon={<CopyCheck />} title="Sin duplicados" description="No se detectaron grupos de duplicados" />;
    }

    return (
        <div className="divide-y divide-surface-100">
            {groups.map((group, idx) => (
                <div key={idx} className="px-5 py-4">
                    <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
                        <div className="min-w-0">
                            <StatusTag tone={group.type === 'exact_code' ? 'danger' : 'warning'}>{group.type}</StatusTag>
                            <div className="mt-2 text-sm font-semibold text-surface-900">{group.reason}</div>
                        </div>
                        <div className="flex gap-2">
                            <Button size="small" icon={<Merge />} onClick={() => onMergeGroup?.(group)}>
                                Fusionar
                            </Button>
                            <Button size="small" type="text" icon={<X />} onClick={() => onDismiss?.(group)}>
                                Descartar
                            </Button>
                        </div>
                    </div>
                    <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
                        {group.products.map((p) => (
                            <ProductCompareCard key={p._id} product={p} onMarkDuplicate={onMarkDuplicate} onRecordClick={onRecordClick} />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
